import React from 'react';

// The design's stroke icons (Claude canvas "Product Page Steps 1-2-3 Test"), inline so they take the text colour. Decorative:
// every one is aria-hidden, the text next to it carries the meaning.

type IconProps = {size?: number; className?: string};

const stroke = (size: number, className: string | undefined, width: number, children: React.ReactNode) => (
    <svg
        className={className}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={width}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
    >
        {children}
    </svg>
);

export const TruckIcon: React.FC<IconProps> = ({size = 18, className}) =>
    stroke(
        size,
        className,
        2,
        <>
            <path d="M2 6.5h11v9H2z" />
            <path d="M13 9.5h4.2l3.3 3.3v2.7H13z" />
            <circle
                cx="6"
                cy="17.5"
                r="1.8"
            />
            <circle
                cx="17"
                cy="17.5"
                r="1.8"
            />
        </>,
    );

export const CheckIcon: React.FC<IconProps> = ({size = 16, className}) => stroke(size, className, 2.6, <path d="M5 12.5l4.5 4.5L19 7.5" />);

export const CheckCircleIcon: React.FC<IconProps> = ({size = 20, className}) =>
    stroke(
        size,
        className,
        2.2,
        <>
            <circle
                cx="12"
                cy="12"
                r="9.5"
            />
            <path d="M8 12.5l2.7 2.7L16 9.8" />
        </>,
    );

export const RulerIcon: React.FC<IconProps> = ({size = 20, className}) =>
    stroke(
        size,
        className,
        2,
        <>
            <path d="M3 8l5-5 13 13-5 5z" />
            <path d="M7 7l2 2M10 4l2 2M13 7l2 2M16 10l2 2" />
        </>,
    );

export const AlertIcon: React.FC<IconProps> = ({size = 16, className}) =>
    stroke(
        size,
        className,
        2.2,
        <>
            <circle
                cx="12"
                cy="12"
                r="9.5"
            />
            <path d="M12 7.5v5.5M12 16.5v.01" />
        </>,
    );

export const CartIcon: React.FC<IconProps> = ({size = 18, className}) =>
    stroke(
        size,
        className,
        2,
        <>
            <path d="M3 4h2.2l2.3 10.1a1 1 0 0 0 1 .8h8.8a1 1 0 0 0 1-.8L20.2 7H6" />
            <circle
                cx="9.6"
                cy="19"
                r="1.4"
            />
            <circle
                cx="17"
                cy="19"
                r="1.4"
            />
        </>,
    );

export const HeartIcon: React.FC<IconProps & {filled?: boolean}> = ({size = 20, className, filled}) => (
    <svg
        className={className}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
    >
        <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z" />
    </svg>
);

export const ChevronDownIcon: React.FC<IconProps> = ({size = 16, className}) => stroke(size, className, 2.5, <path d="M6 9l6 6 6-6" />);

const STAR = 'M12 2.8L14.8 8.7L21.2 9.5L16.5 13.9L17.7 20.2L12 17.1L6.3 20.2L7.5 13.9L2.8 9.5L9.2 8.7Z';

/** Five stars filled to the score, in halves. */
export const Stars: React.FC<{score: number; size?: number}> = ({score, size = 16}) => {
    const halves = Math.round(Math.max(0, Math.min(5, score)) * 2);
    return (
        <span
            className="pp3-stars"
            aria-hidden="true"
        >
            {[0, 1, 2, 3, 4].map(i => {
                const fill = halves >= (i + 1) * 2 ? 'full' : halves === i * 2 + 1 ? 'half' : 'none';
                return (
                    <svg
                        key={i}
                        width={size}
                        height={size}
                        viewBox="0 0 24 24"
                        focusable="false"
                    >
                        <path
                            d={STAR}
                            className={fill === 'full' ? 'pp3-star-on' : 'pp3-star-off'}
                        />
                        {fill === 'half' && (
                            <path
                                d={STAR}
                                className="pp3-star-on"
                                style={{clipPath: 'inset(0 50% 0 0)'}}
                            />
                        )}
                    </svg>
                );
            })}
        </span>
    );
};
